/**
 * Harvesting 2.0 — formatage et libellés, source unique.
 *
 * POURQUOI CE FICHIER EXISTE
 * Avant lui, le module portait :
 *   - `num()` / `round()` en CINQ exemplaires sous DEUX noms, dont un
 *     (SumalApv) au contrat INVERSE — entier, `null → 0` — pendant que celui
 *     d'Overview rendait 2 décimales et `null → « — »`. Deux écrans affichaient
 *     donc deux nombres différents pour la même donnée.
 *   - `date()` en TROIS exemplaires verbatim, plus une variante `date0()`, plus
 *     deux formatages en ligne, plus quatre dates affichées brutes. DataQuality
 *     n'avait aucun helper de date et imprimait de l'ISO.
 *   - SIX libellés pour TROIS valeurs d'énumération : le même `erp` s'affichait
 *     « ERP seul » sur un écran et « XYLO seul » sur le suivant.
 *   - QUATRE précédences pour le nom de parcelle, si bien qu'un même APV portait
 *     deux noms sur deux écrans voisins.
 *
 * RÈGLE UNIQUE : une valeur absente rend « — ». Jamais 0, jamais NaN, jamais ''.
 * Un vrai zéro rend « 0 » — la différence entre « rien mesuré » et « mesuré à
 * zéro » est précisément ce que ce module existe pour ne plus perdre.
 */

export const DASH = '—'

const absent = (v) =>
  v === null || v === undefined || v === '' || (typeof v === 'number' && !isFinite(v))

const fr = (v, min, max) =>
  Number(v).toLocaleString('fr-FR', { minimumFractionDigits: min, maximumFractionDigits: max })

/* ------------------------------------------------------------------ nombres */

/** Générique, précision explicite. */
export const num = (v, decimals = 2) => (absent(v) ? DASH : fr(v, decimals, decimals))

/** m³ en tableau : 1 décimale. Les volumes sont grands, la 2ᵉ est du bruit. */
export const m3 = (v) => (absent(v) ? DASH : fr(v, 1, 1))
/** m³ en tuile : la magnitude, pas la précision. */
export const m3int = (v) => (absent(v) ? DASH : fr(v, 0, 0))
/** RON : 0 décimale, les montants font 5 à 6 chiffres. */
export const ron = (v) => (absent(v) ? DASH : fr(v, 0, 0))
/** Tarifs et prix unitaires. */
export const rate = (v) => (absent(v) ? DASH : fr(v, 2, 2))
export const eur = (v) => (absent(v) ? DASH : fr(v, 2, 2))
export const int = (v) => (absent(v) ? DASH : fr(v, 0, 0))
export const tonnes = (v) => (absent(v) ? DASH : fr(v, 1, 1))

/**
 * Un taux 0..1 vers « 84 % ». On ne borne PAS au-dessus de 100 :
 * une parcelle sortie à 130 % est une anomalie, l'afficher à 100 % la cache.
 */
export const pct = (v, decimals = 0) =>
  absent(v) ? DASH : `${fr(v * 100, decimals, decimals)} %`

/* -------------------------------------------------------------------- dates */

/** JJ/MM/AAAA. Toute la base rend de l'ISO ; l'écran est français. */
export const date = (d) => {
  if (absent(d)) return DASH
  const s = String(d).slice(0, 10)
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s
}

export const dateTime = (d) => {
  if (absent(d)) return DASH
  const t = String(d).slice(11, 16)
  return t ? `${date(d)} à ${t}` : date(d)
}

/**
 * Un instant, lu à l'heure des chantiers (Europe/Bucharest).
 *
 * POURQUOI PAS dateTime
 * `dateTime` découpe la chaîne ISO telle quelle, donc elle affiche de l'UTC. La
 * synchro SUMAL tourne à 03 h 00 UTC, soit 06 h 00 en Roumanie : l'écran
 * annonçait « chaque nuit à 6 h » juste au-dessus d'une dernière synchro
 * horodatée « 03:00 ». Les deux étaient vrais et se contredisaient à l'œil.
 *
 * Réservé aux horodatages de machine (synchro, import). Les dates métier restent
 * en `date` : elles sont déjà des jours, sans heure ni fuseau.
 */
export const dateTimeRo = (d) => {
  if (absent(d)) return DASH
  const dt = new Date(d)
  if (isNaN(dt.getTime())) return dateTime(d)
  try {
    const opts = {
      timeZone: 'Europe/Bucharest',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }
    const p = {}
    new Intl.DateTimeFormat('fr-FR', opts).formatToParts(dt).forEach((x) => { p[x.type] = x.value })
    return `${p.day}/${p.month}/${p.year} à ${p.hour}:${p.minute}`
  } catch (e) {
    // Un navigateur sans base de fuseaux vaut mieux qu'un écran cassé.
    return dateTime(d)
  }
}

/** Pour <vs-input type="date"> : la valeur DOIT rester en ISO. */
export const dateInput = (d) => (absent(d) ? null : String(d).slice(0, 10))
/** Pour l'export XLSX : ISO, seul format triable dans Excel. */
export const dateISO = (d) => (absent(d) ? '' : String(d).slice(0, 10))

/**
 * La prolongation, dite en clair.
 *
 * Quand l'autorisation d'un APV tombe, l'ocol accorde du temps pour évacuer le
 * bois et SUMAL recule la date de démantèlement du dépôt. C'est alors ELLE qui
 * est décomptée. Sans cette phrase, un écran annonçant « 45 jours » sur un
 * permis échu depuis le 17/09 serait incompréhensible.
 */
export const extensionNote = (p) => {
  if (!p || !p.deadline_extended) return ''
  return `autorisation échue le ${date(p.permit_expiry || p.date_permit_expiry)}, ` +
    `prolongé jusqu'au ${date(p.deadline)}`
}

/** « 448 j » ne parle à personne ; « 15 mois » se lit d'un coup d'œil. */
export const duration = (days) => {
  if (absent(days)) return DASH
  const n = Math.abs(Number(days))
  if (n < 60) return `${n} j`
  if (n < 730) return `${Math.round(n / 30)} mois`
  return `${(n / 365).toFixed(1)} ans`
}

/* --------------------------------------------------------- nom de parcelle */

/**
 * `products.name_en` porte le code et parfois des espaces parasites :
 * « OS Barzava - 2300212600670 ». Le code est déjà la clé de la ligne, le
 * répéter dans le libellé ne sert à rien.
 */
export const cleanProductName = (s) =>
  !s
    ? null
    : String(s)
      .replace(/\d{13}/, '')
      .replace(/[-–—_]\s*$/, '')
      .replace(/^\s*[-–—_]\s*/, '')
      .replace(/\s+/g, ' ')
      .trim() || null

/**
 * PRÉCÉDENCE UNIQUE, pour tout le module :
 *   display_name (choisi par un humain) → sumal_label (le nom du dépôt, propre)
 *   → product_name (brut, nettoyé du code)
 */
export const parcelName = (r) =>
  (r && (r.display_name || r.sumal_label || cleanProductName(r.product_name))) || DASH

export const parcelLink = (r) => ({
  name: 'harvesting2-parcel',
  params: { apv: (r && r.apv_code) || r }
})

/* ------------------------------------------------- libellés et tonalités */

const buildMap = (dict, fallbackTone = 'neutral') => ({
  label: (k) => (dict[k] && dict[k].label) || k || DASH,
  tone: (k) => (dict[k] && dict[k].tone) || fallbackTone,
  icon: (k) => (dict[k] && dict[k].icon) || 'CircleIcon',
  items: Object.keys(dict).map((k) => ({ value: k, text: dict[k].label }))
})

/** L'appariement SUMAL ↔ XYLO. UNE formulation, plus six. */
export const SOURCE = buildMap({
  both: { label: 'SUMAL + XYLO', tone: 'ok', icon: 'CheckCircleIcon' },
  sumal: { label: 'SUMAL seul', tone: 'danger', icon: 'AlertCircleIcon' },
  erp: { label: 'XYLO seul', tone: 'warn', icon: 'AlertTriangleIcon' }
})

export const STATUS = buildMap({
  to_exploit: { label: 'À exploiter', tone: 'info', icon: 'ClockIcon' },
  ongoing: { label: 'En cours', tone: 'ok', icon: 'PlayCircleIcon' },
  done: { label: 'Terminé', tone: 'neutral', icon: 'CheckIcon' }
})

export const DECISION = buildMap({
  extract: { label: 'Sortir le bois', tone: 'ok', icon: 'TruckIcon' },
  request_extension: { label: 'Demander une prolongation', tone: 'info', icon: 'CalendarIcon' },
  write_off: { label: 'Acter la perte', tone: 'warn', icon: 'XCircleIcon' }
})

/** Fin des « ACTIVE » / « INACTIVE » bruts affichés à l'utilisateur. */
export const DEPOT_STATUS = buildMap({
  ACTIVE: { label: 'Actif', tone: 'ok', icon: 'CheckCircleIcon' },
  INACTIVE: { label: 'Inactif', tone: 'neutral', icon: 'MinusCircleIcon' },
  SUSPENDED: { label: 'Suspendu', tone: 'warn', icon: 'PauseCircleIcon' }
})

export const COST_TYPE = buildMap({
  road_building: { label: 'Création de route' },
  machine_transport: { label: 'Transport de machines' },
  municipality_fee: { label: 'Taxe de passage mairie' },
  local_arrangement: { label: 'Arrangement population locale' },
  fine: { label: 'Amende' },
  penalty: { label: 'Pénalité' },
  other: { label: 'Autre' }
})

/** Ce qui manque à une fiche pour que la parcelle soit chiffrable. */
export const GAP = buildMap({
  cubage_volume_m3: { label: 'volume autorisé' },
  purchase_price_ron_m3: { label: "prix d'achat" },
  operating_mode: { label: "contrat d'exploitation" },
  logging_rate_ron_m3: { label: "tarif d'exploitation" },
  date_permit_expiry: { label: 'échéance' }
})

export const OPERATING_MODE = buildMap({
  own: { label: 'Régie XYLO' },
  contractor: { label: 'Prestataire' }
})

/* -------------------------------------------------------------- anomalies */

/**
 * Les anomalies d'une parcelle, avec ce qu'elles VEULENT DIRE.
 *
 * Les pastilles nommaient l'anomalie sans jamais l'expliquer : « à encoder »,
 * « 4 produits », « absent de SUMAL » ne disent rien à qui n'a pas construit le
 * module. Chaque entrée porte donc sa phrase, affichée au survol — et une seule
 * définition sert la liste, le détail d'une parcelle et les filtres.
 */
export const anomalies = (r) => {
  if (!r) return []
  const out = []

  if (r.not_encoded) {
    out.push({
      key: 'not_encoded',
      label: 'à encoder',
      tone: 'danger',
      icon: 'PlusCircleIcon',
      title: "L'APV n'existe pas dans XYLO",
      why: "SUMAL a un dépôt pour ce code, XYLO n'a aucun produit. La parcelle existe sur le terrain "
        + "mais n'est rattachée à aucun document : tant que la réception n'est pas créée, aucun volume "
        + "ni aucun coût ne peut lui être attribué."
    })
  }

  if (r.no_purchase_contract) {
    out.push({
      key: 'no_purchase_contract',
      label: 'sans réception',
      tone: 'danger',
      icon: 'FileMinusIcon',
      title: "Du bois est sorti sans facture d'achat",
      why: `${r.avize} aviz ont déclaré du bois à l'État sur cette parcelle, mais aucune réception `
        + "n'existe dans XYLO : ni prix, ni garantie, ni échéancier. Le coût de la parcelle est donc "
        + "inconnu, et elle n'apparaissait dans aucun total de l'écran historique."
    })
  }

  if (r.has_twins) {
    out.push({
      key: 'has_twins',
      label: `${r.products} produits`,
      tone: 'warn',
      icon: 'CopyIcon',
      title: 'Le volume de la parcelle est éclaté',
      why: `Le même code APV existe sur ${r.products} produits différents. Son volume est réparti `
        + "entre eux, et l'écran historique ne comptait que celui portant la réception — c'est ce qui "
        + "faisait afficher un avancement trop faible. La fusion se lance depuis Qualité & synchro."
    })
  }

  if (!r.in_sumal) {
    out.push({
      key: 'not_in_sumal',
      label: 'absent de SUMAL',
      tone: 'warn',
      icon: 'CloudOffIcon',
      title: 'Aucun dépôt SUMAL pour ce code',
      why: "XYLO porte un produit pour ce code, mais le registre SUMAL n'a aucun dépôt correspondant. "
        + "Le plus souvent, le code inscrit dans le nom du produit est erroné — un chiffre de trop ou "
        + "de moins. Sans dépôt, la parcelle n'a ni date d'ouverture, ni échéance, ni statut."
    })
  }

  if (!r.sheet_complete && r.avize > 0 && r.status !== 'done') {
    const manque = (r.sheet_gaps || []).map((g) => GAP.label(g)).join(', ')
    out.push({
      key: 'sheet',
      label: 'fiche incomplète',
      tone: 'warn',
      icon: 'EditIcon',
      title: 'La parcelle n’est pas chiffrable',
      why: `Il manque : ${manque}. Sans ces valeurs, le coût du mètre cube rendu usine ne peut pas être `
        + "calculé. Ces champs sont humains — SUMAL ne les porte pas — et se saisissent dans l'onglet Fiche."
    })
  }

  // Plus de bois sorti que la réception n'en a acheté : un fait mesuré, que rien
  // ne signalait alors qu'il s'affiche en clair dans la colonne Avancement.
  if (r.extraction_rate !== null && r.extraction_rate > 1.02) {
    out.push({
      key: 'over',
      label: `${Math.round(r.extraction_rate * 100)} % sorti`,
      tone: 'warn',
      icon: 'TrendingUpIcon',
      title: 'Plus sorti que acheté',
      why: "Le volume sorti dépasse le volume de la réception. Soit le produit sert de fourre-tout à "
        + "plusieurs parcelles, soit le volume autorisé saisi est inférieur à la réalité. À examiner, "
        + "pas à conclure."
    })
  }

  return out
}

/* --------------------------------------------------------------- échéance */

/**
 * La tonalité d'une échéance. Un permis permanent (01-01-2100, les dépôts fixes)
 * n'est pas une échéance : le décompter donnerait 27 000 jours.
 */
export const deadlineTone = (days, permanent) => {
  if (permanent) return 'none'
  if (absent(days)) return 'none'
  if (days < 0) return 'past'
  if (days <= 15) return 'urgent'
  if (days <= 30) return 'soon'
  return 'far'
}

export default {
  DASH,
  num,
  m3,
  m3int,
  ron,
  rate,
  eur,
  int,
  tonnes,
  pct,
  date,
  dateTime,
  dateTimeRo,
  extensionNote,
  dateInput,
  dateISO,
  duration,
  cleanProductName,
  parcelName,
  parcelLink,
  deadlineTone,
  anomalies,
  SOURCE,
  STATUS,
  DECISION,
  DEPOT_STATUS,
  COST_TYPE,
  GAP,
  OPERATING_MODE
}
